import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSupabase } from '../contexts/SupabaseContext';
import { Camera, Mail, User, Calendar, Trophy, AlertCircle, Save, Trash2, Loader2 } from 'lucide-react';
import Avatar from '../components/common/Avatar';
import { getProxiedImageUrl, getMimeTypeFromFile } from '../utils/imageUtils';
import imageCompression from 'browser-image-compression';

interface Profile {
  id: string;
  nome: string;
  nome_completo: string;
  avatar_url: string | null;
  data_nascimento: string | null;
  telefone: string | null;
  endereco: string | null;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { supabase } = useSupabase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [imageError, setImageError] = useState(false);

  const [nomeCompleto, setNomeCompleto] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  const [telefone, setTelefone] = useState('');
  const [endereco, setEndereco] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setProfile(data);
          setNomeCompleto(data.nome_completo || '');
          setDataNascimento(data.data_nascimento || '');
          setTelefone(data.telefone || '');
          setEndereco(data.endereco || '');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();
  }, [user, supabase]);

const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (!file || !user) {
    console.log('No file selected or user not logged in');
    return;
  }

  let previewUrl: string | null = null;

  console.log('=== VERIFICAÇÃO DE INTEGRIDADE DO ARQUIVO ===');
  
  // 1. Verificar propriedades básicas do arquivo
  const fileInfo = {
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified,
    webkitRelativePath: file.webkitRelativePath
  };
  console.log('📄 Propriedades do arquivo:', fileInfo);
  
  // 2. Criar preview visual do arquivo
  try {
    previewUrl = URL.createObjectURL(file);
    console.log('🖼️ Preview URL criada:', previewUrl);
    
    // Atualizar o avatar imediatamente com o preview
    setProfile((prev) => prev ? { ...prev, avatar_url: previewUrl } : null);
    
    // Testar se a imagem pode ser carregada
    const testImg = new Image();
    await new Promise((resolve, reject) => {
      testImg.onload = () => {
        console.log('✅ Arquivo pode ser carregado como imagem:', {
          width: testImg.naturalWidth,
          height: testImg.naturalHeight,
          complete: testImg.complete
        });
        resolve(true);
      };
      testImg.onerror = (error) => {
        console.error('❌ Arquivo não pode ser carregado como imagem:', error);
        reject(new Error('Arquivo não é uma imagem válida'));
      };
      testImg.src = previewUrl;
    });
  } catch (previewError) {
    console.error('❌ Erro ao criar preview:', previewError);
    setError('Arquivo selecionado não é uma imagem válida');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    return;
  }
  
  // 3. Ler o arquivo como ArrayBuffer e verificar integridade
  try {
    console.log('📖 Lendo arquivo como ArrayBuffer...');
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    
    const integrityInfo = {
      arrayBufferSize: arrayBuffer.byteLength,
      uint8ArrayLength: uint8Array.length,
      firstBytes: Array.from(uint8Array.slice(0, 10)),
      lastBytes: Array.from(uint8Array.slice(-10)),
      hasContent: uint8Array.length > 0
    };
    
    console.log('🔍 Verificação de integridade:', integrityInfo);
    
    // Verificar apenas se o arquivo tem conteúdo
    if (!integrityInfo.hasContent) {
      throw new Error('Arquivo está vazio');
    }
    
  } catch (integrityError) {
    console.error('❌ Erro na verificação de integridade:', integrityError);
    setError('Arquivo vazio ou corrompido');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    return;
  }
  
  // 4. Ler como Base64 para comparação
  try {
    console.log('📝 Convertendo para Base64...');
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    
    console.log('📝 Base64 gerado:', {
      length: base64.length,
      prefix: base64.substring(0, 50),
      mimeType: base64.split(',')[0]
    });
  } catch (base64Error) {
    console.error('❌ Erro ao converter para Base64:', base64Error);
  }
  setUploading(true);
  setImageError(false);
  setError(null);

  try {
    console.log('=== INÍCIO DO UPLOAD DIRETO VIA FETCH ===');
    
    // Obter token de autenticação
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    
    // Gerar nome único para o arquivo
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${crypto.randomUUID()}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;
    
    console.log('📡 Configurações do upload:', {
      filePath,
      fileName,
      fileSize: file.size,
      fileType: file.type,
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL
    });
    
    // Construir URL do endpoint do Supabase Storage
    const uploadUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/avatars/${filePath}`;
    console.log('📡 URL de upload:', uploadUrl);
    
    // Fazer upload direto via fetch
    const uploadResponse = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': file.type,
        'x-upsert': 'true'
      },
      body: file
    });
    
    console.log('📡 Resposta do upload:', {
      status: uploadResponse.status,
      statusText: uploadResponse.statusText,
      ok: uploadResponse.ok,
      headers: Object.fromEntries(uploadResponse.headers.entries())
    });
    
    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('❌ Erro na resposta:', errorText);
      throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`);
    }
    
    const uploadResult = await uploadResponse.json();
    console.log('✅ Resultado do upload:', uploadResult);
    
    // Construir URL pública
    const publicUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/avatars/${filePath}`;
    console.log('✅ URL pública construída:', publicUrl);
    
    // Testar carregamento da imagem SEM proxy primeiro
    console.log('🔍 Testando carregamento direto (sem proxy)...');
    const testImageDirect = new Image();
    
    try {
      await new Promise((resolve, reject) => {
        testImageDirect.onload = () => {
          console.log('✅ Imagem carregada DIRETAMENTE com sucesso:', {
            width: testImageDirect.naturalWidth,
            height: testImageDirect.naturalHeight,
            src: testImageDirect.src
          });
          resolve(true);
        };
        
        testImageDirect.onerror = (error) => {
          console.error('❌ Erro ao carregar imagem DIRETAMENTE:', error);
          reject(new Error('Imagem não pode ser carregada diretamente'));
        };
        
        testImageDirect.src = publicUrl;
      });
    } catch (directLoadError) {
      console.error('❌ Falha no carregamento direto:', directLoadError);
      
      // Se falhar o carregamento direto, testar com proxy
      console.log('🔍 Testando carregamento com proxy...');
      const testImageProxy = new Image();
      
      await new Promise((resolve, reject) => {
        testImageProxy.onload = () => {
          console.log('✅ Imagem carregada com PROXY:', {
            width: testImageProxy.naturalWidth,
            height: testImageProxy.naturalHeight,
            src: testImageProxy.src
          });
          resolve(true);
        };
        
        testImageProxy.onerror = (error) => {
          console.error('❌ Erro ao carregar imagem com PROXY:', error);
          reject(new Error('Imagem não pode ser carregada nem com proxy'));
        };
        
        testImageProxy.src = getProxiedImageUrl(publicUrl);
      });
    }

    console.log('💾 Salvando URL no banco de dados...');
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', user.id);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw updateError;
    }

    console.log('✅ Perfil atualizado no banco de dados');
    
    // Forçar atualização do estado local
    setProfile((prev) => prev ? { ...prev, avatar_url: publicUrl } : null);
    
    // Limpar preview da memória (mas manter a URL no estado)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
    
    console.log('=== UPLOAD DIRETO CONCLUÍDO COM SUCESSO ===');
    
    // Recarregar a página para atualizar o avatar em todos os componentes
    setTimeout(() => {
      window.location.reload();
    }, 1000); // Aguarda 1 segundo para mostrar a mensagem de sucesso
  } catch (error: any) {
    console.error('❌ ERRO NO UPLOAD DA IMAGEM:', error);
    console.error('Detalhes do erro:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    setImageError(true);
    setError(`Falha ao enviar imagem: ${error.message}`);
    
    // Limpar preview em caso de erro e restaurar avatar original
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setProfile((prev) => prev ? { ...prev, avatar_url: profile?.avatar_url || null } : null);
  } finally {
    setUploading(false);
  }
};







  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4,5})(\d{4})$/, '$1-$2');
    }
    return value;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          nome: nomeCompleto,
          nome_completo: nomeCompleto,
          data_nascimento: dataNascimento || null,
          telefone: telefone || null,
          endereco: endereco || null
        })
        .eq('id', user.id);

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setDeletingAccount(true);
    setError(null);

    try {
      const response = await fetch('/.netlify/functions/delete-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao excluir conta');
      }

      await signOut();
      navigate('/login');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setError(error.message || 'Erro ao excluir conta');
      setShowDeleteConfirm(false);
    } finally {
      setDeletingAccount(false);
    }
  };

  if (!user || !profile) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <p>Carregando...</p>
      </div>
    );
  }

  const avatarUrl = profile.avatar_url ? getProxiedImageUrl(profile.avatar_url) : null;

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Meu Perfil</h1>
        <p className="text-gray-500 dark:text-gray-400">Gerencie suas informações pessoais</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-center gap-6 mb-8">
              <div className="relative">
                {avatarUrl && !imageError ? (
                  <Avatar url={avatarUrl} name={profile.nome || 'User'} size="xl" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                    <User className="w-12 h-12 text-primary-600 dark:text-primary-400" />
                  </div>
                )}
                <label
                  htmlFor="avatar-upload"
                  className="absolute bottom-0 right-0 p-2 bg-white dark:bg-dark-paper rounded-full shadow-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-light transition-colors"
                >
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                    disabled={uploading}
                  />
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </label>
              </div>

              <div>
                <h2 className="text-xl font-bold">{profile.nome}</h2>
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Mail className="w-4 h-4" />
                  <span>{user.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mt-1">
                  <Calendar className="w-4 h-4" />
                  <span>Membro desde {new Date(user.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
                {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
                {success && <p className="mt-2 text-sm text-green-600 dark:text-green-400">Perfil atualizado com sucesso!</p>}
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <input
                  type="text"
                  value={nomeCompleto}
                  onChange={(e) => setNomeCompleto(e.target.value)}
                  className="input"
                  placeholder="Nome completo"
                />
                <input type="date" value={dataNascimento} onChange={(e) => setDataNascimento(e.target.value)} className="input" />
                <input
                  type="tel"
                  value={telefone}
                  onChange={(e) => setTelefone(formatPhoneNumber(e.target.value))}
                  className="input"
                  placeholder="(00) 00000-0000"
                />
                <input
                  type="text"
                  value={endereco}
                  onChange={(e) => setEndereco(e.target.value)}
                  className="input"
                  placeholder="Endereço completo"
                />
                <input type="email" value={user.email} disabled className="input bg-gray-100 dark:bg-dark-light" />
              </div>

              <div className="mt-6 flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir minha conta
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div>
          <div className="card p-6">
            <div className="flex items-center gap-3 text-primary-600 dark:text-primary-400">
              <Trophy className="w-5 h-5" />
              <h3 className="text-lg font-bold">Presida.Club</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Versão 1.0.0</p>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400 mb-4">
              <AlertCircle className="w-6 h-6" />
              <h3 className="text-lg font-bold">Excluir Conta</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Tem certeza que deseja excluir sua conta? Esta ação é irreversível.
            </p>
            <div className="flex justify-end gap-4">
              <button onClick={() => setShowDeleteConfirm(false)} className="btn-outline" disabled={deletingAccount}>
                Cancelar
              </button>
              <button
                onClick={handleDeleteAccount}
                className="bg-red-600 text-white py-2 px-4 rounded-lg flex items-center gap-2"
                disabled={deletingAccount}
              >
                {deletingAccount ? 'Excluindo...' : 'Sim, excluir minha conta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage; 